/** Display policy only: feature names remain the byte-exact raw filter predicates. */
export const MAX_FEATURE_FACET_LABEL_LENGTH = 40

// Deliberately explicit. A short sentence or an unfamiliar supplier heading is
// not automatically a technical feature. Extend this vocabulary after review.
const TECHNICAL_LABELS = new Set(`
Reversible door, recessed handle
Recessed handle & flush back
Accu-Chill™ temperature management
ADA Compliant
Door-in-Door
Smart Features
Touch Screen
Camera Inside
Craft Ice
Water Dispenser
Temperature Alarm
Lock
Adjustable Thermostat
Power-On Indicator Light
Drain for Defrost Water
Interior Basket
Frost Free
AI Family Hub™
AI Family Hub™+
AI Home - 9" Screen
AI Vision Inside
AI Vision Inside™
Adaptive Defrost
Adjustable / removable shelves
Adjustable Door Bins
Adjustable Gallon Door Bins
Adjustable Shelves
Adjustable Spill-Resistant Glass Shelves
Adjustable Full-Width Glass Shelves
Adjustable Glass Shelves
Adjustable Temperature Control
Adjustable Temperature Drawer
Adjustable Thermostat Control
Advanced Water Filtration
All-Around Cooling
Auto Open Door
Auto Open Door feature
Auto-Close Doors
AutoFill Water Pitcher
Automatic Ice Maker
Automatic defrost
Beverage Center
Beverage Center™
Bonus Freezer Shelf
Bright LED Lighting
BrightSeries™ LED Lighting
Built-In Freezer Drawers
Built-In WiFi
Built-in / undercounter installation
Built-in ice maker
CanStor Beverage Dispensing System
Clear Door Bins
Clear Door Shelves
Clear crisper drawers
Convertible
Counter Depth Design
Counter-depth design
CrispSeal® Plus Crisper
Customizable Door Colors
Customizable Door Colours
Customizable door panels
Dairy Bin
Dairy Compartment
Dairy Shelf
Deli Drawer
Digital Display
Digital Inverter Technology
Digital Temperature Controls
Door Ajar Alarm
Door Ajar and High Temp Alarm
Door Alarm
Door-in-Door Storage
Door-in-Door / InstaView panel
Dual Auto Ice Maker
Dual Ice Maker
Dual-Dispense AutoFill Pitcher
Dual-zone temperature control
Energy Star Certified
Energy Star® Certified
Energy Star qualified
EZ Connect Icemaker Kit
Easy Access Icemaker
Edge-to-Edge Shelves
Electronic Display
Electronic Temperature Controls
Electronic/Digital Temperature Controls
Elevated Deli Drawer
Enhanced Shabbos Mode
EvenTemp™ Cooling System
External Dispenser
External Water & Ice Dispenser
Factory-Installed Icemaker
Factory-installed ice maker
Fast Freeze
Fingerprint Resistant
Fingerprint Resistant Finish
Fingerprint-resistant finish
Fingerprint Resistant Stainless
Fingerprint Resistant Stainless Steel
Fingerprint Resistant Stainless Finish
Flex Zone™
FlexZone™ Drawer
Flexi-Slide™ Bin
Flexible Freezer Divider
Flexible Temperature Drawer
Flip-up Storage
Floor-Projected Power-On Indicator
Frameless Glass Shelves
Freezer Temperature Controls
Fresh Storage Crispers
FreshFlow™ Air Filter
Frost-Free Auto-Defrost
Frost-Free Design
Frost-free operation
Full range temperature control
Full-Width Crisper Drawer
Full-Width Pantry Drawer
Gallon Door Bin
Gallon Door Bins
Gallon Door Storage
Gallon Door Storage Bins
Gallon door storage
Garage Ready
Garage-ready installation
Glass Shelves
Hidden Hinges
High-Efficiency LED Lighting
High-gloss handles
Humidity-Controlled Crispers
Humidity-controlled crisper drawers
Ice Maker
Ice Maker Ready
Ice & Water Dispenser
Icemaker-ready
In-Door Ice Maker
In-Door-Ice Maker
In-Door-Ice® Storage
In-Door-Ice® System
Incandescent Lighting
Integrated Door Shelving
Integrated door handle
Integrated security lock
Interior Storage Drawers
Internal Water Dispenser
Internal Water Filter
Intuitive Controls
Inverter Compressor
LED Dispenser Night-Light
LED Interior Lighting
LED Light Wall
LED Lighting
Large Door Bin Storage
Lock with Key
Low-noise compressor
Manual defrost
Max Cool
Measured Fill
Mechanical thermostat
Metal Cooling
Multi Air Flow
Optional Ice Maker
Optional Icemaker
Optional Icemaker Kit
Optional Automatic Icemaker Kit
Panel-ready cabinet
Panoramic LED Lighting
Pizza Pocket
Preserva® Food Care System
PrintShield® Finish
PureAir Ultra® II Air Filter
PureAir® Air Filter RAF-2™
PurePour™ Water and Ice Filter
Reversible Door
Reversible Doors
Reversible door hinge
Reversible door swing
Reversible door, left or right swing
Sabbath Mode
Sabbath Mode (Star-K Certified)
SatinGlide® System with Soft-Close
Signature Platinum Interior
Slide-Out Freezer Basket
Smart & Energy Star Rated
SmartThings AI Energy Mode
SmartThings integration
SmartThings app integration
SmartThings compatibility
Smooth back design
Smudge-Proof® Stainless Steel
Soft Close Drawers
SpaceWise® Organization System
Spillproof Glass Shelves
Spill-proof shelves
Star-K Certified Sabbath Mode
Temp Assure™ Freshness Controls
Through-the-door water dispenser
Top Freezer
Total Coverage Cooling
Twin Cooling Plus™
Twin Cooling Plus™ technology
TwinChill™ Dual Evaporators
Two-Tier Freezer Storage
Ultra-Bright LED Lighting
Up-Front Temperature Control System
Up-front electronic temperature controls
Upfront temperature controls
Wi-Fi / smart home compatible
Wi-Fi Connectivity
Wi-Fi Enabled
WiFi Connect
everydrop® Water Filter
French door design
Standard Depth Design
Removable Bin Caddy
Filtered Water Capacity
Monitor Ounces Dispensed
Tap Touch Controls
Infinity Slide Shelf
NSF 42 certified
NSF 42 and 53 certified
Reduces chlorine, lead, and cysts
Reduces chlorine, taste/odor
2-Tier Freezer Drawer
4 Adjustable Glass Shelves
`.trim().split('\n').map(label => label.toLowerCase()))

// Finishes are a closed palette, never arbitrary text followed by "finish".
const FINISHES = new Set(`
Black
Blue
Cream
Green
Grey
Gray
Red
Silver
White
Solid Black
Marshmallow White
Pastel Blue
Pastel Green
Summer Mint Green
Stainless Steel
Stainless Steel Look
Black Stainless Steel
Black & Stainless Steel
Silver & Black
Stainless Steel & White
Stainless Steel & Black
Stainless Steel / Black
Stainless Steel/black
Panel Ready
Panel-ready
Glass/black
`.trim().split('\n').map(label => `${label.toLowerCase()} finish`))

function isTechnicalLabel(label: string): boolean {
  if (!label || label.length > MAX_FEATURE_FACET_LABEL_LENGTH || label !== label.trim()) return false
  if (/\b(?:our|we|your|you)\b/i.test(label)) return false
  const lower = label.toLowerCase()
  return TECHNICAL_LABELS.has(lower) || FINISHES.has(lower)
    || /^(?:[1-9]\d*|0\.[1-9]\d*|[1-9]\d*\.\d+) cu\. ft\. (?:freezer |total )?capacity$/i.test(label)
    || /^[1-9]\d?(?:\.\d+)?-inch width$/i.test(label)
    || /^[1-9]\d* gallon capacity$/i.test(label)
}

// Reviewed whole-label equivalences, not word extraction or new product claims.
const TECHNICAL_ALIASES = new Map<string, string>([
  ['Reversible door with recessed door handle', 'Reversible door, recessed handle'],
  ['Space-saving recessed door handle and flush back design', 'Recessed handle & flush back'],
  ['Accu-Chill™ Temperature Management System', 'Accu-Chill™ temperature management'],
])

/** Never searches for a feature buried in prose or strips a negated qualifier. */
export function featureFacetLabel(name: string): string | null {
  if (isTechnicalLabel(name)) return name
  const alias = TECHNICAL_ALIASES.get(name)
  if (alias && alias.length <= MAX_FEATURE_FACET_LABEL_LENGTH) return alias
  const separator = /:\s*|\s+[-–—]\s*/.exec(name)
  if (!separator) return null
  const prefix = name.slice(0, separator.index).trim()
  const explanation = name.slice(separator.index + separator[0].length)
  if (!explanation.trim() || !isTechnicalLabel(prefix)) return null
  // Keep qualifiers such as optional/not included from becoming positive claims.
  // This intentionally also drops benign prose containing these ambiguous words.
  if (/\b(?:no|not|never|without|optional|sold separately|ready|unavailable|absent|excluded|doesn['’]t|isn['’]t|aren['’]t|can['’]t|won['’]t)\b/i.test(explanation)) return null
  return prefix
}

export interface RawFeatureOption { name: string; count: number }
export interface FeatureFacetOption extends RawFeatureOption { displayLabel?: string }

/** Preserve short predicates; hide ambiguous shortened siblings, never merge counts. */
export function toFeatureFacetOptions(options: readonly RawFeatureOption[]): FeatureFacetOption[] {
  const labeled = options.map(option => ({ option, label: featureFacetLabel(option.name) }))
  const groups = new Map<string, Set<string>>()
  for (const { option, label } of labeled) {
    if (label === null) continue
    const key = label.toLowerCase()
    const names = groups.get(key) ?? new Set<string>()
    names.add(option.name)
    groups.set(key, names)
  }
  return labeled.flatMap(({ option, label }): FeatureFacetOption[] => {
    if (label === null) return []
    if (label === option.name) return [{ name: option.name, count: option.count }]
    if ((groups.get(label.toLowerCase())?.size ?? 0) > 1) return []
    return [{ name: option.name, count: option.count, displayLabel: label }]
  })
}

/** Original raw vocabulary, including occurrence counts and threshold semantics. */
export function rawFeatureOptions(
  products: readonly { features?: readonly (string | null | undefined)[] | null }[],
): RawFeatureOption[] {
  const counts = new Map<string, number>()
  for (const product of products) {
    for (const feature of product.features || []) {
      const name = (feature || '').trim()
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1)
    }
  }
  const minCount = Math.max(3, Math.round(products.length * 0.05))
  return [...counts.entries()]
    .filter(([, count]) => count >= minCount)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => ({ name, count }))
}
